/**
 * Error-related TypeScript type definitions
 * 
 * @module types/error
 */

/**
 * Application error codes
 */
export enum AppErrorCode {
  // Network errors
  NetworkError = 'NETWORK_ERROR',
  TimeoutError = 'TIMEOUT_ERROR',
  ConnectionError = 'CONNECTION_ERROR',
  
  // API errors
  ApiError = 'API_ERROR',
  BadRequest = 'BAD_REQUEST',
  Unauthorized = 'UNAUTHORIZED',
  Forbidden = 'FORBIDDEN',
  NotFound = 'NOT_FOUND',
  ValidationError = 'VALIDATION_ERROR',
  ServerError = 'SERVER_ERROR',
  
  // Payment errors
  PaymentError = 'PAYMENT_ERROR',
  PaymentDeclined = 'PAYMENT_DECLINED',
  PaymentCancelled = 'PAYMENT_CANCELLED',
  PaymentFailed = 'PAYMENT_FAILED',
  
  // Configuration errors
  ConfigurationError = 'CONFIGURATION_ERROR',
  MissingCompanyId = 'MISSING_COMPANY_ID',
  InvalidConfiguration = 'INVALID_CONFIGURATION',
  
  // Data errors
  DataError = 'DATA_ERROR',
  InvalidData = 'INVALID_DATA',
  MissingData = 'MISSING_DATA',
  
  // Unknown
  UnknownError = 'UNKNOWN_ERROR',
}

/**
 * Application error class
 */
export class AppError extends Error {
  code: AppErrorCode;
  details?: Record<string, unknown>;
  timestamp: string;

  constructor(
    code: AppErrorCode,
    message: string,
    details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'AppError';
    this.code = code;
    this.details = details;
    this.timestamp = new Date().toISOString();
    
    // Maintains proper stack trace for where error was thrown (V8 only)
    if ((Error as any).captureStackTrace) {
      (Error as any).captureStackTrace(this, AppError);
    }
  }
}

/**
 * Network error class
 */
export class NetworkError extends AppError {
  constructor(message: string, details?: Record<string, unknown>) {
    super(AppErrorCode.NetworkError, message, details);
    this.name = 'NetworkError';
  }
}

/**
 * API error class
 */
export class ApiError extends AppError {
  statusCode?: number;

  constructor(
    message: string,
    statusCode?: number,
    details?: Record<string, unknown>,
  ) {
    super(AppErrorCode.ApiError, message, details);
    this.name = 'ApiError';
    this.statusCode = statusCode;
  }
}

/**
 * Payment error class
 */
export class PaymentError extends AppError {
  paymentProvider?: string;
  
  constructor(
    message: string,
    paymentProvider?: string,
    details?: Record<string, unknown>
  ) {
    super(AppErrorCode.PaymentError, message, details);
    this.name = 'PaymentError';
    this.paymentProvider = paymentProvider;
  }
}

/**
 * Validation error class
 */
export class ValidationError extends AppError {
  field?: string;
  
  constructor(
    message: string,
    field?: string,
    details?: Record<string, unknown>
  ) {
    super(AppErrorCode.ValidationError, message, details);
    this.name = 'ValidationError';
    this.field = field;
  }
}

/**
 * Error response from API
 */
export interface ErrorResponse {
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
    timestamp?: string;
    path?: string;
  };
}

/**
 * User-friendly error messages
 */
export const ERROR_MESSAGES: Record<AppErrorCode, string> = {
  [AppErrorCode.NetworkError]: 'Unable to connect to the server. Please check your internet connection.',
  [AppErrorCode.TimeoutError]: 'Request timed out. Please try again.',
  [AppErrorCode.ConnectionError]: 'Connection error. Please try again.',
  [AppErrorCode.ApiError]: 'An error occurred while processing your request.',
  [AppErrorCode.BadRequest]: 'Invalid request. Please check your input.',
  [AppErrorCode.Unauthorized]: 'You are not authorized to perform this action.',
  [AppErrorCode.Forbidden]: 'Access denied.',
  [AppErrorCode.NotFound]: 'The requested resource was not found.',
  [AppErrorCode.ValidationError]: 'Please check your input and try again.',
  [AppErrorCode.ServerError]: 'Server error. Please try again later.',
  [AppErrorCode.PaymentError]: 'Payment processing error. Please try again.',
  [AppErrorCode.PaymentDeclined]: 'Your payment was declined. Please try another payment method.',
  [AppErrorCode.PaymentCancelled]: 'Payment was cancelled.',
  [AppErrorCode.PaymentFailed]: 'Payment failed. Please try again.',
  [AppErrorCode.ConfigurationError]: 'Configuration error. Please contact support.',
  [AppErrorCode.MissingCompanyId]: 'Invalid donation link. Please use the correct link provided by your organization.',
  [AppErrorCode.InvalidConfiguration]: 'Invalid configuration. Please contact support.',
  [AppErrorCode.DataError]: 'Data error. Please try again.',
  [AppErrorCode.InvalidData]: 'Invalid data received. Please try again.',
  [AppErrorCode.MissingData]: 'Required data is missing. Please try again.',
  [AppErrorCode.UnknownError]: 'An unexpected error occurred. Please try again.',
};

/**
 * Get user-friendly error message
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof AppError) {
    return ERROR_MESSAGES[error.code] || error.message;
  }
  
  if (error instanceof Error) {
    return error.message;
  }
  
  return ERROR_MESSAGES[AppErrorCode.UnknownError];
}

/**
 * Type guard for AppError
 */
export function isAppError(error: unknown): error is AppError {
  return error instanceof AppError;
}

/**
 * Type guard for ErrorResponse
 */
export function isErrorResponse(obj: unknown): obj is ErrorResponse {
  if (typeof obj !== 'object' || obj === null) return false;
  
  const response = obj as Record<string, unknown>;
  
  return (
    typeof response.error === 'object' &&
    response.error !== null &&
    typeof (response.error as Record<string, unknown>).code === 'string' &&
    typeof (response.error as Record<string, unknown>).message === 'string'
  );
}
